import React from "react";
import { CheckCircle2, AlertCircle, Phone, Mail } from "lucide-react";

export function VerifiedMobileInput({
  label = "Mobile Number *",
  value,
  onChange,
  required = true,
  placeholder = "9876543210",
  className = "",
}) {
  const digitsOnly = (value || "").replace(/\D/g, "");
  const digitCount = digitsOnly.length;
  const isValid = digitCount === 10 && /^[6-9]/.test(digitsOnly);

  const handleChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 10);
    onChange(raw);
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-400 font-semibold">
          {label}
        </label>
        <span
          className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-full border transition-all ${
            isValid
              ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
              : digitCount > 0
              ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
              : "bg-slate-800 text-slate-500 border-slate-700"
          }`}
        >
          {digitCount}/10 Digits {isValid && "✓ Valid"}
        </span>
      </div>

      <div className="relative">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-mono font-semibold text-slate-400">
          +91
        </span>
        <input
          type="tel"
          required={required}
          maxLength={10}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          className={`w-full h-11 pl-12 pr-10 rounded-xl bg-slate-950 border text-sm text-white font-mono placeholder-slate-600 focus:outline-none transition-all ${
            isValid
              ? "border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/30"
              : digitCount > 0
              ? "border-amber-500/60 focus:ring-2 focus:ring-amber-500/30"
              : "border-slate-800 focus:ring-2 focus:ring-primary/40"
          } ${className}`}
        />
        <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
          {isValid ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          ) : digitCount > 0 ? (
            <AlertCircle className="w-4 h-4 text-amber-400" />
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function VerifiedEmailInput({
  label = "Email Address",
  value,
  onChange,
  required = false,
  placeholder = "name@example.com",
  className = "",
}) {
  const emailVal = (value || "").trim();
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const isValid = emailVal.length > 0 && emailRegex.test(emailVal);
  const isInvalidFormat = emailVal.length > 0 && !isValid;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-400 font-semibold">
          {label}
        </label>
        {emailVal.length > 0 && (
          <span
            className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-full border transition-all ${
              isValid
                ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                : "bg-red-500/20 text-red-400 border-red-500/30"
            }`}
          >
            {isValid ? "Valid Email Format ✓" : "Invalid Format"}
          </span>
        )}
      </div>

      <div className="relative">
        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          type="email"
          required={required}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full h-11 pl-10 pr-10 rounded-xl bg-slate-950 border text-sm text-white font-mono placeholder-slate-600 focus:outline-none transition-all ${
            isValid
              ? "border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/30"
              : isInvalidFormat
              ? "border-red-500/60 focus:ring-2 focus:ring-red-500/30"
              : "border-slate-800 focus:ring-2 focus:ring-primary/40"
          } ${className}`}
        />
        <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
          {isValid ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          ) : isInvalidFormat ? (
            <AlertCircle className="w-4 h-4 text-red-400" />
          ) : null}
        </div>
      </div>
    </div>
  );
}
